import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Activity, ShieldAlert, ArrowRight } from 'lucide-react';
export function TrackSpend() {
  return <div className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto relative" data-id="element-438">
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl h-64 bg-emerald-500/5 blur-[120px] pointer-events-none rounded-full" data-id="element-439"></div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center mb-20 relative z-10" data-id="element-440">
        <h1 className="text-5xl md:text-6xl font-bold text-slate-50 mb-8 tracking-tight" data-id="element-441">
          How to Track AI Coding Spend
        </h1>
        <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto" data-id="element-442">
          A practical framework for engineering leaders to measure, attribute,
          and optimize their investment in AI coding assistants.
        </p>
      </motion.div>

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-443">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-2xl text-slate-300 mb-16 leading-relaxed font-medium" data-id="element-444">
          The average enterprise wastes 30% of its AI tooling budget on idle
          seats and inefficient API usage. As tools like Copilot, Cursor, and
          Claude become standard issue, tracking this spend is no longer
          optional—it's a core engineering operations metric.
        </motion.p>

        <div className="my-20 grid sm:grid-cols-3 gap-6" data-id="element-445">
          {[{
          icon: Activity,
          title: '1. Measure',
          desc: 'Track active daily usage, not just provisioned seats.'
        }, {
          icon: BarChart,
          title: '2. Attribute',
          desc: 'Map costs to specific teams, projects, and cost centers.'
        }, {
          icon: ShieldAlert,
          title: '3. Optimize',
          desc: 'Automatically reclaim idle licenses and set budget alerts.'
        }].map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-slate-900 p-8 rounded-2xl border border-slate-800 shadow-xl text-center" data-id="element-446">
              <div className="mx-auto w-14 h-14 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 mb-6 glow-box-emerald" data-id="element-447">
                <step.icon size={24} data-id="element-448" />
              </div>
              <h3 className="font-bold text-slate-50 mb-3 text-lg" data-id="element-449">
                {step.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed" data-id="element-450">
                {step.desc}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-451">
          <h2 className="text-3xl font-bold text-slate-50 mt-16 mb-8" data-id="element-452">
            The "Seat Waste" Problem
          </h2>
          <p className="text-lg text-slate-400 mb-8 leading-relaxed" data-id="element-453">
            Most engineering organizations treat AI tools like standard SaaS:
            they buy a license for every developer. However, coding patterns
            vary. Senior architects might spend 80% of their time in design docs
            and code review, rarely triggering autocomplete. Junior developers
            might lean heavily on chat features.
          </p>
          <div className="bg-slate-900 border-l-4 border-emerald-500 p-8 rounded-r-xl mb-12 shadow-lg relative overflow-hidden" data-id="element-454">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent pointer-events-none" data-id="element-455"></div>
            <p className="text-xl text-slate-300 italic leading-relaxed relative z-10" data-id="element-456">
              "We bought 500 Copilot Enterprise seats at $39/mo. After
              implementing tracking, we found 140 developers hadn't accepted a
              suggestion in over 30 days. That was $65,000 in annual waste."
            </p>
            <p className="text-emerald-400 font-bold mt-4 relative z-10" data-id="element-457">
              — VP Engineering, FinTech
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-458">
          <h2 className="text-3xl font-bold text-slate-50 mt-16 mb-10" data-id="element-459">
            Implementing a Tracking Strategy
          </h2>

          <div className="space-y-12" data-id="element-460">
            <div data-id="element-461">
              <h3 className="text-2xl font-bold text-slate-200 mb-4 flex items-center gap-3" data-id="element-462">
                <span className="text-emerald-500 font-mono" data-id="element-463">01.</span> Define
                "Active Usage"
              </h3>
              <p className="text-lg text-slate-400 mb-6 leading-relaxed" data-id="element-464">
                Simply having the IDE plugin installed is not usage. You must
                track telemetry for:
              </p>
              <ul className="space-y-4 text-slate-300 text-lg" data-id="element-465">
                <li className="flex items-center gap-3" data-id="element-466">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 glow-box-emerald" data-id="element-467"></div>{' '}
                  Accepted completions (lines of code generated)
                </li>
                <li className="flex items-center gap-3" data-id="element-468">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 glow-box-emerald" data-id="element-469"></div>{' '}
                  Chat queries submitted
                </li>
                <li className="flex items-center gap-3" data-id="element-470">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 glow-box-emerald" data-id="element-471"></div>{' '}
                  API tokens consumed (for consumption models)
                </li>
              </ul>
            </div>

            <div data-id="element-472">
              <h3 className="text-2xl font-bold text-slate-200 mb-4 flex items-center gap-3" data-id="element-473">
                <span className="text-emerald-500 font-mono" data-id="element-474">02.</span>{' '}
                Centralize Telemetry
              </h3>
              <p className="text-lg text-slate-400 leading-relaxed" data-id="element-475">
                If your team uses a mix of tools (e.g., Cursor for frontend,
                Copilot for backend), you need a unified dashboard. Relying on
                vendor-specific billing pages makes cross-tool comparison
                impossible.
              </p>
            </div>

            <div data-id="element-476">
              <h3 className="text-2xl font-bold text-slate-200 mb-4 flex items-center gap-3" data-id="element-477">
                <span className="text-emerald-500 font-mono" data-id="element-478">03.</span> Automate
                License Reclamation
              </h3>
              <p className="text-lg text-slate-400 leading-relaxed" data-id="element-479">
                Set up automated workflows: If a developer drops below 5
                accepted completions in a 14-day window, automatically downgrade
                their license or return it to the pool.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-24 bg-slate-900 border border-emerald-500/30 rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden shadow-2xl" data-id="element-480">
          <div className="absolute inset-0 bg-emerald-500/5 pointer-events-none" data-id="element-481"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.15)_0%,transparent_70%)] pointer-events-none" data-id="element-482"></div>

          <h2 className="text-4xl font-bold mb-6 text-slate-50 relative z-10 tracking-tight" data-id="element-483">
            Automate this entire process.
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed relative z-10" data-id="element-484">
            TokenMeter by Waydev connects to your Git provider and AI tools to
            automatically track utilization, calculate ROI, and eliminate seat
            waste.
          </p>
          <a href="https://waydev.co/pricing" className="inline-flex items-center gap-3 bg-emerald-500 text-slate-950 px-8 py-4 rounded-xl font-bold hover:bg-emerald-400 transition-colors glow-box-emerald text-lg relative z-10" data-id="element-485">
            Start Tracking with TokenMeter <ArrowRight size={20} data-id="element-486" />
          </a>
        </motion.div>
      </div>
    </div>;
}